import { useState, CSSProperties, useEffect, useMemo } from "react";

// Interface
import { State, Event, SetProp, PickedState} from "./MeGenInterface";

// Types
import {
  ProjectPayload,
  CARD_NAME,
  SeriesSerializer,
  UpdateItemPayload,
  UpdateMetadataPayload,
  StoryInput,
  FullPartsInput,
  FinalScriptData,
  DirectScriptInput,
  SeriesType,
  RunStateSerializer,
  ShareProjectPayload,
  ShareMemberSerializer,
  DeleteSharedMemberPayload,
  ShareResource,
  ShareRunMapData,
  UpdateCompositionPayload,
  AiUsageThreadDetail,
} from "./types/MeGenTypes";

// Components
import { canEditProject } from "./common/projectRole";
import { RunsFilter, RunsSliceKey } from "./common/runsQuery";
import MeGenSidebarMUI from "./common/MeGenSidebarMUI";
import DimmerLoader from "./common/DimmerLoader";
import ModCreateProject from "./common/ModCreateProject";
import ModConfirmDelete from "./common/ModConfirmDelete";
import CardProjectDetail from "./CardProjectDetail";
import CardGenerateVideo from "./CardGenerateVideo";
import CardGenerateScript from "./CardGenerateScript";
import MeGenRunView from "./MeGenRunView";
import ModCreateSeriesWebsite from "./ModCreateSeriesWebsite";
import ModCreateSeriesScript from "./ModCreateSeriesScript";
import ModShareProject from "./ModShareProject";
import SnackMessage from "../common/SnackMessage";

// Serializers
import ProfiledDivisionHasUserSerializer from 'issara-sdk/types/ProfiledDivisionHasUserSerializer_apps_PRX'

type CardMeGenMainProps = {
  MeGenSequence: State["MeGenSequence"];
  onEvent: (e: Event) => void;
  setProp: SetProp;
  runSequence: any;
  BillingAndPackageSequence: any;
  selectedDivisionId: any;
  canViewAiUsage?: boolean;
} & Pick<
  PickedState, "loadingStatus" | "errorMessage" | "successMessage" | "selectedDivision"
>;

const CardMeGenMain = (props: CardMeGenMainProps) => {
  // Sidebar Expanded / Collapsed State
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);

  // Modal State (Create/Edit)
  const [openProjectMod, setOpenProjectMod] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editProjectId, setEditProjectId] = useState<number | undefined>(undefined);
  const [editProjectName, setEditProjectName] = useState<string>("");

  // Modal State (Delete Project)
  const [openDeleteProjectMod, setOpenDeleteProjectMod] = useState<boolean>(false);
  const [deleteProjectId, setDeleteProjectId] = useState<number | undefined>(undefined);
  const [deleteProjectName, setDeleteProjectName] = useState<string>("");

  // Modal State (View Series)
  const [viewSeriesData, setViewSeriesData] = useState<SeriesSerializer | undefined>(undefined);
  const [openViewWebsiteMod, setOpenViewWebsiteMod] = useState<boolean>(false);
  const [openViewScriptMod, setOpenViewScriptMod] = useState<boolean>(false);

  // Modal State (Edit Series)
  const [editSeriesData, setEditSeriesData] = useState<SeriesSerializer | undefined>(undefined);

  // Modal State (Delete Series)
  const [openDeleteSeriesMod, setOpenDeleteSeriesMod] = useState<boolean>(false);
  const [deleteSeries, setDeleteSeries] = useState<SeriesSerializer | undefined>(undefined);

  // Modal State (Confirm Delete Series)
  const [openConflictDeleteSeriesMod, setOpenConflictDeleteSeriesMod] = useState<boolean>(false);
  const [conflictSeriesMessage, setConflictSeriesMessage] = useState<string>("");

  // Modal State (Rerun Episode)
  const [openRerunEpisodeMod, setOpenRerunEpisodeMod] = useState<boolean>(false);
  const [rerunThreadId, setRerunThreadId] = useState<string | undefined>(undefined);

  // Modal State (Duplicate Episode)
  const [openDuplicateEpisodeMod, setOpenDuplicateEpisodeMod] = useState<boolean>(false);
  const [duplicateThreadId, setDuplicateThreadId] = useState<string | undefined>(undefined);

  // Modal State (Delete Episode)
  const [openDeleteEpisodeMod, setOpenDeleteEpisodeMod] = useState<boolean>(false);
  const [deleteThreadId, setDeleteThreadId] = useState<string | undefined>(undefined);

  // Modal Create From Website Series
  const [openCreateWebsiteMod, setOpenCreateWebsiteMod] = useState<boolean>(false);

  // Modal Create From Script Series
  const [openCreateScriptMod, setOpenCreateScriptMod] = useState<boolean>(false);

  // Modal State (Share Project)
  const [shareResourceType, setShareResourceType] = useState<ShareResource>("project");
  const [openShareProjectMod, setOpenShareProjectMod] = useState<boolean>(false);
  const [shareProjectId, setShareProjectId] = useState<number | undefined>(undefined);
  const [shareProjectName, setShareProjectName] = useState<string>("");
  const [shareMembers, setShareMembers] = useState<ShareMemberSerializer[]>([]);
  const [shareSearchResults, setShareSearchResults] = useState<ProfiledDivisionHasUserSerializer[]>([]);
  const [isSearhingUsers, setIsSearchingUsers] = useState<boolean>(false);

  // Modal State (Share Series)
  const [shareSeriesId, setShareSeriesId] = useState<number | null>(null);
  const [shareSeriesName, setShareSeriesName] = useState<string>("");

  // Modal State (Share Runmap)
  const [shareRunMapId, setShareRunMapId] = useState<string | null>(null);
  const [shareRunMapName, setShareRunMapName] = useState<string>("");

  useEffect(() => {
    props.onEvent({
      message: "HandleStartMeGen",
      params: {
        restart: true,
      },
    });
  }, [props.selectedDivision]);

  const handleOpenCreateProjectMod = () => {
    setIsEditMode(false);
    setEditProjectId(undefined);
    setEditProjectName("");
    setOpenProjectMod(true);
  };

  const handleOpenEditProjectMod = (projectId: number, projectName: string) => {
    setIsEditMode(true);
    setEditProjectId(projectId);
    setEditProjectName(projectName);
    setOpenProjectMod(true);
  };

  const handleCloseProjectMod = () => {
    setOpenProjectMod(false);
    setIsEditMode(false);
    setEditProjectId(undefined);
    setEditProjectName("");
  };

  // -- Delete Project Mod Handlers -- //
  const handleOpenDeleteProjectMod = (projectId: number, projectName: string) => {
    setDeleteProjectId(projectId);
    setDeleteProjectName(projectName);
    setOpenDeleteProjectMod(true);
  };

  const handleCloseDeleteProjectMod = () => {
    setOpenDeleteProjectMod(false);
    setDeleteProjectId(undefined);
    setDeleteProjectName("");
  };

  // -- Delete Series Mod Handlers -- //
  const handleOpenDeleteSeriesMod = (series: SeriesSerializer) => {
    setDeleteSeries(series);
    setOpenDeleteSeriesMod(true);
  };

  const handleCloseDeleteSeriesMod = () => {
    setOpenDeleteSeriesMod(false);
  };

  // -- Rerun Episode Mod Handlers -- //
  const handleOpenRerunEpisode = (episodeId: string) => {
    setRerunThreadId(episodeId);
    setOpenRerunEpisodeMod(true);
  };

  const handleCloseRerunEpisode = () => {
    setRerunThreadId(undefined);
    setOpenRerunEpisodeMod(false);
  };

  // -- Duplicate Episode Mod Handlers -- //
  const handleOpenDuplicateEpisode = (episodeId: string) => {
    setDuplicateThreadId(episodeId);
    setOpenDuplicateEpisodeMod(true);
  };

  const handleCloseDuplicateEpisode = () => {
    setDuplicateThreadId(undefined);
    setOpenDuplicateEpisodeMod(false);
  };

  // -- Delete Episode Mod Handlers -- //
  const handleOpenDeleteEpisode = (episodeId: string) => {
    setDeleteThreadId(episodeId);
    setOpenDeleteEpisodeMod(true);
  };

  const handleCloseDeleteEpisode = () => {
    setDeleteThreadId(undefined);
    setOpenDeleteEpisodeMod(false);
  };

  const handleCreateProject = async (projectName: string) => {

    const newProject: ProjectPayload = {
      name: projectName,
      // description: "",
    };

    props.onEvent({
      message: "HandleCreateProject",
      params: {
        data: newProject,
        onSuccess: () => {
        }
      },
    });
  };

  const handleEditProject = (projectId: number, projectName: string) => {
    const editedProject: ProjectPayload = {
      name: projectName,
      // description: "",
    };

    props.onEvent({
      message: "HandleEditProject",
      params: {
        projectId,
        data: editedProject,
        onSuccess: () => {
          handleCloseProjectMod();
        },
      },
    });
  };

  const handleConfirmDeleteProject = () => {
    if (deleteProjectId === undefined) return;

    props.onEvent({
      message: "HandleDeleteProject",
      params: {
        projectId: deleteProjectId,
        onSuccess: () => {},
      },
    });

    handleCloseDeleteProjectMod();
  };

  const handleConfirmDeleteSeries = () => {
    if (!deleteSeries?.id) return;

    props.onEvent({
      message: "HandleDeleteSeries",
      params: {
        projectId: deleteSeries.project_id,
        seriesId: deleteSeries.id,
        onSuccess: () => {
          setDeleteSeries(undefined);
        },
        onConflict: (message: string) => {
          handleCloseDeleteSeriesMod();
          setConflictSeriesMessage(message);
          setOpenConflictDeleteSeriesMod(true);
        },
      },
    });

    handleCloseDeleteSeriesMod();
  };

  const handleConfirmForceDeleteSeries = () => {
    if (!deleteSeries?.id) return;

    props.onEvent({
      message: "HandleDeleteSeries",
      params: {
        projectId: deleteSeries.project_id,
        seriesId: deleteSeries.id,
        confirm: true,
        onSuccess: () => {
          setDeleteSeries(undefined);
        },
      },
    });

    setOpenConflictDeleteSeriesMod(false);
  };

  const handleSelectProject = (projectId: number) => {
    props.onEvent({
      message: "HandleGetSeriesList",
      params: {
        projectId,
      },
    });
  };

  // -- Open View Series Detail Mod Handlers -- //
  const handleViewSeries = (series: SeriesSerializer) => {
    setViewSeriesData(series);

    if (series.series_type === "rss_news") {
      setOpenViewWebsiteMod(true);
    } else {
      setOpenViewScriptMod(true);
    }
  };

  const handleCloseViewWebsiteMod = () => {
    setOpenViewWebsiteMod(false);
    setViewSeriesData(undefined);
  };

  const handleCloseViewScriptMod = () => {
    setOpenViewScriptMod(false);
    setViewSeriesData(undefined);
  };

  // -- Open Create Website Series Mod Handlers -- //
  const handleOpenCreateWebsiteMod = () => {
    setOpenCreateWebsiteMod(true);
  };

  const handleCloseCreateWebsiteMod = () => {
    setOpenCreateWebsiteMod(false);
    setEditSeriesData(undefined);
  };

  // -- Open Create Script Series Mod Handlers -- //
  const handleOpenCreateScriptMod = () => {
    setOpenCreateScriptMod(true);
  };

  const handleCloseCreateScriptMod = () => {
    setOpenCreateScriptMod(false);
    setEditSeriesData(undefined);
  };

  const handleSaveSeriesFromModal = (data: SeriesSerializer) => {
    handleSaveSeries(data);
  };

  // -- Edit Series Handlers -- //
  const handleOpenEditWebsiteMod = (series: SeriesSerializer) => {
    setEditSeriesData(series);
    setOpenCreateWebsiteMod(true);
  };

  const handleOpenEditScriptMod = (series: SeriesSerializer) => {
    setEditSeriesData(series);
    setOpenCreateScriptMod(true);
  };

  const handleEditSeries = (series: SeriesSerializer) => {
    if (series.series_type === "rss_news") {
      handleOpenEditWebsiteMod(series);
    } else {
      handleOpenEditScriptMod(series);
    }
  };

  // -- Save Series Handlers -- //
  const handleSaveSeries = (data: SeriesSerializer) => {
    if (data.id) {
      const { series_type, ...cleanPayload } = data;

      props.onEvent({
        message: "HandleEditSeries",
        params: {
          projectId: data.project_id,
          seriesId: data.id,
          data: cleanPayload,
          onSuccess: () => {},
        },
      });

      handleCloseCreateWebsiteMod();
      handleCloseCreateScriptMod();
    } else {
      props.onEvent({
        message: "HandleCreateSeries",
        params: {
          data,
          onSuccess: () => {},
        },
      });

      handleCloseCreateWebsiteMod();
      handleCloseCreateScriptMod();
    }
  };

  // --- Navigation Handlers
  const handleClearMessage = () => {
    props.setProp(`errorMessage.${CARD_NAME}`, null);
    props.setProp(`successMessage.${CARD_NAME}`, null);
  };

  const handleCreateFromScript = () => {
    props.setProp("MeGenSequence", {
      ...props.MeGenSequence,
      selectedSeries: undefined,
      currentView: "create-from-script",
    });
  };

  const handleCreateFromWebsite = () => {
    props.setProp("MeGenSequence", {
      ...props.MeGenSequence,
      selectedSeries: undefined,
      currentView: "create-from-website"
    });
  };

  const handleNavigateBackToProject = () => {
    props.onEvent({
      message: "HandleNaviagateToProjectPage",
      params: {},
    });
  };

  // --- Episode Handlers ---
  const handleSelectEpisode = (episodeId: string, runType?: SeriesType) => {
    props.onEvent({
      message: "HandleSelectEpisode",
      params: {
        episodeId,
        runType,
      },
    });
    setIsSidebarCollapsed(true);
  };

  /**
   * ค้น/เรียง/โหลดเพิ่ม run ของ slice หนึ่ง — ส่ง `filter` ครบชุดไปกับ event เสมอ
   * (handler ห้ามอ่าน filter กลับจาก state เพราะ setState ของ controller ถูก batch)
   * แท็บ Videos → runsVideos · แผง episode ของ Playlist → runsPlaylist
   * (filter.seriesId = series ที่เลือก, null = ปิดแผง)
   */
  const searchRunsFor = (slice: RunsSliceKey) => (filter: RunsFilter, mode?: "replace" | "append") => {
    const projectId = props.MeGenSequence?.selectedProject?.id;

    if (!projectId) return;

    props.onEvent({
      message: "HandleSearchRuns",
      params: { projectId, filter, mode: mode || "replace", slice },
    });
  };
  const handleSearchRuns = searchRunsFor("runsVideos");
  const handleSearchPlaylistRuns = searchRunsFor("runsPlaylist");

  const handleConfirmRestartEpisode = () => {
    if (!rerunThreadId) return;

    props.onEvent({
      message: "HandleRerunRun",
      params: {
        threadId: rerunThreadId,
      },
    });

    handleCloseRerunEpisode();
  };

  const handleConfirmDuplicateEpisode = () => {
    if (!duplicateThreadId) return;

    props.onEvent({
      message: "HandleDuplicateRun",
      params: {
        threadId: duplicateThreadId,
      },
    });

    handleCloseDuplicateEpisode();
  };

  const handleConfirmDeleteEpisode = () => {
    if (!deleteThreadId) return;

    props.onEvent({
      message: "HandleDeleteRun",
      params: {
        threadId: deleteThreadId,
        onSuccess: () => {},
      },
    });

    handleCloseDeleteEpisode();
  };

  // --- Generate Video Handlers --- //
  const handleCreateNewVideo = (seriesId?: number) => {
    const series = seriesId
      ? (props.MeGenSequence?.seriesList || []).find(s => s.id === seriesId)
      : undefined;

    props.setProp("MeGenSequence", {
      ...props.MeGenSequence,
      currentView: "generate-video",
      selectedSeries: series || undefined,
      currentThreadId: undefined,
      graphState: undefined,
    });
    setIsSidebarCollapsed(true);
  };

  const handleStartGenerate = (dryRun: boolean, seriesId: number | null) => {
    props.onEvent({
      message: "HandleCreateVideo",
      params: {
        dryRun,
        seriesId,
        manualScript: null,
        onSuccess: () => {
        }
      }
    });
  };

  const handleStopGenerate = () => {
    const threadId = props.MeGenSequence?.currentThreadId;

    if (!threadId) {
      console.error("Error: No ThreadId selected or generating");
      return;
    }

    props.onEvent({
      message: "HandleStopRun",
      params: { threadId }
    });
  };

  const handleResumeUpload = (threadId: string, credentialId: number | null) => {
    props.onEvent({
      message: "HandleUploadYouTube",
      params: {
        threadId,
        credentialId
      },
    });
  };

  const handleUpdateItem = (
    threadId: string,
    itemId: string,
    data: UpdateItemPayload,
    onSuccess?: (newState: RunStateSerializer, isGenerating: boolean) => void
  ) => {
    const payload: UpdateItemPayload = {
      ...data,
      item_id: itemId,
    };

    props.onEvent({
      message: "HandleUpdateVideoItem",
      params: {
        threadId,
        data: payload,
        onSuccess,
      }
    });
  };

  const handleUpdateMetaData = (
    threadId: string,
    data: UpdateMetadataPayload,
    onSuccess?: (newState: RunStateSerializer, isGenerating: boolean) => void
  ) => {
    props.onEvent({
      message: "HandleUpdateMetadata",
      params: {
        threadId,
        data,
        onSuccess,
      },
    });
  };

  const handleUpdateComposition = (
    threadId: string,
    data: UpdateCompositionPayload,
    onSuccess?: (newState: RunStateSerializer, isGenerating: boolean) => void
  ) => {
    props.onEvent({
      message: "HandleUpdateComposition",
      params: { threadId, data, onSuccess },
    });
  };

  // --- Generate Script Handlers --- //
  const handleCreateNewScript = (seriesId?: number) => {
    const series = seriesId
      ? (props.MeGenSequence?.seriesList || []).find(s => s.id === seriesId)
      : undefined;

    props.setProp("MeGenSequence", {
      ...props.MeGenSequence,
      currentView: "generate-script",
      selectedSeries: series || undefined,
      currentThreadId: undefined,
      graphState: undefined,
    });
    setIsSidebarCollapsed(true);
  };

  const handleCreateStoryToSceneRun = async (projectId: number, seriesId: number): Promise<string | null> => {
    return new Promise((resolve) => {
      props.onEvent({
        message: "HandleCreateStory2SceneRun",
        params: {
          projectId,
          seriesId,
          onSuccess: (threadId: string) => resolve(threadId),
        },
      });
    });
  };

  const handleSubmitStory = (threadId: string, story: StoryInput) => {
    props.onEvent({
      message: "HandleSubmitStory",
      params: { threadId, story },
    });
  };

  const handleSubmitParts = (threadId: string, parts: FullPartsInput) => {
    props.onEvent({
      message: "HandleSubmitParts",
      params: { threadId, parts },
    });
  };

  const handleGenerateVideoFromScript = (threadId: string, script: FinalScriptData, dryRun: boolean) => {
    props.onEvent({
      message: "HandleGenerateVideoFromScript",
      params: { threadId, script, dryRun },
    });
  }

  // --- Generate Direct Script Handlers --- //
  const handleDirectGenerate = (
    threadId: string | null,
    projectId: number,
    seriesId: number,
    data: DirectScriptInput,
    dryRun: boolean
  ) => {
    props.onEvent({
      message: "HandleDirectGenerate",
      params: { threadId, projectId, seriesId, data, dryRun },
    });
  };

  const handleCreateNewDirectScript = (seriesId?: number) => {
    const series = seriesId
      ? (props.MeGenSequence?.seriesList || []).find(s => s.id === seriesId)
      : undefined;

    props.setProp("MeGenSequence", {
      ...props.MeGenSequence,
      currentView: "generate-direct-script",
      selectedSeries: series || undefined,
      currentThreadId: undefined,
      graphState: undefined,
    });
    setIsSidebarCollapsed(true);
  };

  const handleFetchAiUsage = (
    threadId: string,
    onSuccess: (detail: AiUsageThreadDetail) => void,
    onError: (message: string) => void,
  ) => {
    props.onEvent({
      message: "HandleGetAiUsageThreadDetail",
      params: { threadId, onSuccess, onError },
    });
  };

  // --- Script To Scene Handlers --- //
  // --- Share Project Handlers --- //
  const handleOpenShareProjectMod = (projectId: number, projectName: string) => {
    setShareResourceType("project");
    setShareProjectId(projectId);
    setShareProjectName(projectName);
    setShareSeriesId(null);
    setShareSeriesName("");
    setShareRunMapId(null);
    setShareRunMapName("");
    setOpenShareProjectMod(true);

    props.onEvent({
      message: "HandleGetShareMembers",
      params: {
        resourceType: "project",
        projectId,
        onSuccess: (members: ShareMemberSerializer[]) => {
          setShareMembers(members);
        },
      },
    });
  };

  const handleCloseShareProjectMod = () => {
    setOpenShareProjectMod(false);
    setShareResourceType("project");
    setShareProjectId(undefined);
    setShareProjectName("");
    setShareSeriesId(null);
    setShareSeriesName("");
    setShareRunMapId(null);
    setShareRunMapName("");
    setShareRunMapId(null);
    setShareRunMapName("");
    setShareMembers([]);
    setShareSearchResults([]);
  };

  const handleSearchUser = (query: string) => {
    setIsSearchingUsers(true);

    props.onEvent({
      message: "HandleSearchUsers",
      params: {
        query,
        onSuccess: (results: ProfiledDivisionHasUserSerializer[]) => {
          setShareSearchResults(results);
          setIsSearchingUsers(false);
        },
      },
    });
  };

  const handleSaveShareMember = (payload: ShareProjectPayload) => {
    props.onEvent({
      message: "HandleShareProject",
      params: {
        data: payload,
        onSuccess: () => {
          if (shareProjectId) {
            props.onEvent({
              message: "HandleGetShareMembers",
              params: {
                resourceType: "project",
                projectId: shareProjectId,
                onSuccess: (members: ShareMemberSerializer[]) => {
                  setShareMembers(members);
                },
              },
            });
          }
        },
      },
    });
  };

  const handleRemoveShareMembers = (userIds: string[]) => {
    if (!shareProjectId || userIds.length === 0) return;

    const payload: DeleteSharedMemberPayload = {
      resource_type: shareResourceType,
      project_id: shareProjectId,
      series_id: shareResourceType === "series" ? (shareSeriesId ?? null) : null,
      thread_id: shareResourceType === "runmap" ? (shareRunMapId ?? null) : null,
      user_ids: userIds,
    };

    props.onEvent({
      message: "HandleRemoveShareMember",
      params: {
        data: payload,
        onSuccess: () => {
          setShareMembers(prev => prev.filter(m => !userIds.includes(m.user_id)));
        },
      },
    });
  };

  // --- Share Series Handlers --- //
  const handleOpenShareSeriesMod = (series: SeriesSerializer) => {
    setShareResourceType("series");
    setShareProjectId(series.project_id);
    setShareProjectName("");
    setShareSeriesId(series.id ?? null);
    setShareSeriesName(series.name);
    setShareRunMapId(null);
    setShareRunMapName("");
    setOpenShareProjectMod(true);

    props.onEvent({
      message: "HandleGetShareMembers",
      params: {
        resourceType: "series",
        projectId: series.project_id,
        seriesId: series.id,
        onSuccess: (members: ShareMemberSerializer[]) => {
          setShareMembers(members);
        },
      },
    });
  };

  // --- Share RunMap (Episode) Handlers --- //
  const handleOpenShareRunMapMod = (shareData: ShareRunMapData) => {
    setShareResourceType("runmap");
    setShareProjectId(shareData.project_id);
    setShareProjectName("");
    setShareSeriesId(null);
    setShareSeriesName("");
    setShareRunMapId(shareData.id ?? null);
    setShareRunMapName(shareData.name);
    setOpenShareProjectMod(true);

    props.onEvent({
      message: "HandleGetShareMembers",
      params: {
        resourceType: "runmap",
        projectId: shareData.project_id,
        threadId: shareData.id,
        onSuccess: (members: ShareMemberSerializer[]) => {
          setShareMembers(members);
        },
      },
    });
  };

  // --- YoutTube Handlers --- //
  const handleConnectYouTube = () => {
    props.onEvent({
      message: "HandleConnectYouTube",
      params: {
        onSuccess: (authUrl: string) => {
          // window.location.href = authUrl;
          window.open(authUrl, "_blank");
        },
        onError: () => {},
      },
    });
  };

  const handleGetYouTubeStatus = () => {
    props.onEvent({
      message: "HandleGetYouTubeStatus",
      params: {},
    });
  };

  const handleDeactivateYouTube = (channelId: string) => {
    props.onEvent({
      message: "HandleDeactivateYouTube",
      params: {
        channelId,
        onSuccess: () => {},
        onError: () => {},
      },
    });
  };

  // --- Character Handlers --- //
  const selectedProject = props.MeGenSequence?.selectedProject;
  const currentProject = (props.MeGenSequence?.projectList || []).find((p) => p.id === selectedProject?.id);
  // role จาก GET /projects | /projects/{id} — "viewer" = หน้าเว็บห้ามยิง endpoint ที่ต้องเป็น editor เองโดยไม่มี user กด
  const canEdit = canEditProject(selectedProject?.role);
  const selectedSeries = props.MeGenSequence?.selectedSeries;
  const isEditSeriesMode = !!selectedSeries?.id;
  const defaultConfig = props.MeGenSequence?.defaultConfig;
  const allOaUsersList = props.MeGenSequence?.allOaUsersList;

  const sidebarProjects = (props.MeGenSequence?.projectList).map(project => ({
    id: project.id,
    name: project.name,
    is_shared: project.is_shared,
    owner_user_id: project.user_id,
  }));

  const ownerNameMap = useMemo(() => {
    const map: Record<string, string> = {};

    (allOaUsersList || []).forEach((u) => {
      const userId = String(u.user);
      const displayName =
        u.user_profile?.fullname ||
        u.user_profile?.username ||
        `User ${userId}`;

      if (userId) map[userId] = displayName;
    });

    return map;
  }, [allOaUsersList]);

  const currentOwnerName = currentProject?.is_shared && currentProject?.user_id
    ? ownerNameMap[currentProject.user_id]
    : undefined;


  const paddingStyle: CSSProperties = {
    paddingLeft: isSidebarCollapsed ? "2px" : "2px",
    paddingRight: "4px",
    transition: "padding-left 0.3s ease",
    height: "100%"
  };

  const renderContent = () => {
    if (props.MeGenSequence?.projectList.length === 0) {
      return (
        <div id="megen-no-project-state" style={paddingStyle}>
          <div
            style={{
              textAlign: "center",
              padding: "1rem",
              color: "#64748b",
              borderRadius: "0.75rem",
              marginTop: "2rem",
            }}
          >
            <p style={{ fontSize: "1rem", marginBottom: "0.5rem" }}>
              คุณยังไม่มี Project หากต้องการสร้างคลิกปุ่ม <strong>New Project</strong>
            </p>
          </div>
        </div>
      );
    }

    if (!selectedProject?.id) {
      return (
        <div id="megen-no-selection-state" style={paddingStyle}>
          <div
            style={{
              textAlign: "center",
              padding: "1rem",
              color: "#64748b",
              borderRadius: "0.75rem",
              marginTop: "2rem",
            }}
          >
            <p style={{ fontSize: "1.2rem", marginBottom: "0.5rem" }}>
              กรุณาเลือก Project หรือกด <strong>New Project</strong> เพื่อเริ่มต้น
            </p>
          </div>
        </div>
      );
    }

    switch (props.MeGenSequence.currentView) {
      case "generate-video":
        return (
          <div id="megen-view-generate-video" style={paddingStyle}>
            <CardGenerateVideo
              projectId={selectedProject?.id}
              onFetchAiUsage={handleFetchAiUsage}
              canViewAiUsage={props.canViewAiUsage}
              aiUsageUserNameMap={ownerNameMap}
              projectName={currentProject?.name || ""}
              seriesId={selectedSeries?.id}
              seriesName={selectedSeries?.name}
              seriesList={(props.MeGenSequence?.seriesList || []).filter(s => s.series_type === "rss_news").map(s => ({ id: s.id!, name: s.name }))}
              threadId={props.MeGenSequence?.currentThreadId || null}
              isDryRun={true}
              isRunning={props.MeGenSequence?.isGenerating || false}
              youtubeChannels={props.MeGenSequence?.youtubeStatus?.channels || []}
              onNavigateBack={handleNavigateBackToProject}
              onStartRun={handleStartGenerate}
              onStopRun={handleStopGenerate}
              onResumeUpload={handleResumeUpload}
              onRerun={handleOpenRerunEpisode}
              onUpdateItem={handleUpdateItem}
              onUpdateMetadata={handleUpdateMetaData}
              onUpdateComposition={handleUpdateComposition}
              onGeneratingChange={(isGenerating) => {
                props.setProp("MeGenSequence.isGenerating", isGenerating);
              }}
              //
              onEvent={props.onEvent}
            />
          </div>
        );

      case "generate-script":
        return (
          <div id="megen-view-generate-script" style={paddingStyle}>
            <CardGenerateScript
              projectId={selectedProject?.id}
              onFetchAiUsage={handleFetchAiUsage}
              canViewAiUsage={props.canViewAiUsage}
              aiUsageUserNameMap={ownerNameMap}
              projectName={currentProject?.name || ""}
              seriesId={selectedSeries?.id}
              seriesName={selectedSeries?.name}
              threadId={props.MeGenSequence?.currentThreadId || null}
              isRunning={props.MeGenSequence?.isGenerating || false}
              onNavigateBack={handleNavigateBackToProject}
              onCreateRun={handleCreateStoryToSceneRun}
              onSubmitStory={handleSubmitStory}
              onSubmitParts={handleSubmitParts}
              onGenerateVideo={handleGenerateVideoFromScript}
              onUploadYoutube={handleResumeUpload}
              onGeneratingChange={(isGenerating) => {
                props.setProp("MeGenSequence.isGenerating", isGenerating);
              }}
              //
              onEvent={props.onEvent}
            />
          </div>
        );

      case "generate-direct-script":
        return (
          <div id="megen-view-generate-direct-script" style={paddingStyle}>
            <MeGenRunView
              MeGenSequence={props.MeGenSequence}
              onEvent={props.onEvent}
              setProp={props.setProp}
              runSequence={props.runSequence}
              BillingAndPackageSequence={props.BillingAndPackageSequence}
              selectedDivisionId={props.selectedDivisionId}
              canViewAiUsage={props.canViewAiUsage}
            />
          </div>
        );


      case "project-detail":
      default:
        return (
          <div id="megen-view-project-detail" style={paddingStyle}>
            <CardProjectDetail
              projectId={selectedProject?.id}
              projectName={currentProject?.name || ""}
              seriesList={props.MeGenSequence?.seriesList || []}
              runs={props.MeGenSequence?.runsVideos}
              runsPlaylist={props.MeGenSequence?.runsPlaylist}
              runsTotal={props.MeGenSequence?.runsTotal ?? 0}
              onSearchRuns={handleSearchRuns}
              onSearchPlaylistRuns={handleSearchPlaylistRuns}
              isRefreshing={props.MeGenSequence?.isProjectDetailRefreshing ?? false}
              ownerName={currentOwnerName}
              // run section
              onCreateVdoFromWebsite={handleCreateNewVideo}
              onCreateVdoFromStory={handleCreateNewScript}
              onCreateVdoFromScript={handleCreateNewDirectScript}
              onSelectEpisode={handleSelectEpisode}
              onRestartEpisode={handleOpenRerunEpisode}
              onDuplicateEpisode={handleOpenDuplicateEpisode}
              onDeleteEpisode={handleOpenDeleteEpisode}
              onShareSeries={handleOpenShareSeriesMod}
              // series section
              onCreateFromScript={handleOpenCreateScriptMod}
              onCreateFromWebsite={handleOpenCreateWebsiteMod}
              onViewSeries={handleViewSeries}
              onEditSeries={handleEditSeries}
              onDeleteSeries={handleOpenDeleteSeriesMod}
              onShareEpisode={handleOpenShareRunMapMod}
              // connection
              onConnectYouTube={handleConnectYouTube}
              onDeactivateYouTube={handleDeactivateYouTube}
              youtubeStatus={props.MeGenSequence?.youtubeStatus}
            />
          </div>
        );
    }
  };

  return (
    <div
      id="megen-root"
      style={{
        display: "flex",
        height: "100%",
        width: "100%",
        position: "relative",
        overflow: "hidden"
      }}
    >

      {/* --- Error & Success Message --- */}
      {/* เคยถูก comment ทิ้งเพราะ toast เด้งมั่ว (ขอ version ก่อน asset มี + poll ฟ้องซ้ำทุก 30 วิ)
          ต้นเหตุถูกแก้แล้ว (common/segmentVersions + failStatePoll) จึงเปิดกลับมาแจ้ง error/success ได้ */}
      <SnackMessage
        error={props.errorMessage?.[CARD_NAME]}
        success={props.successMessage?.[CARD_NAME]}
        onClose={handleClearMessage}
      />

      {/* --- Sidebar --- */}
      <MeGenSidebarMUI
        projects={sidebarProjects}
        activeProjectId={selectedProject?.id}
        collapsed={isSidebarCollapsed}
        ownerNameMap={ownerNameMap}
        onNewProject={handleOpenCreateProjectMod}
        onSelectProject={handleSelectProject}
        onEditProject={(projectId, projectName) => {
          handleOpenEditProjectMod(projectId, projectName);
        }}
        onShareProject={(projectId, projectName) => {
          handleOpenShareProjectMod(projectId, projectName);
        }}
        onDeleteProject={(projectId, projectName) => {
          handleOpenDeleteProjectMod(projectId, projectName);
        }}
        onCollapseChange={(isCollapse) => {
          setIsSidebarCollapsed(isCollapse)
        }}
      />

      <div
        id="megen-content-area"
        style={{
          flex: 1,
          overflow: "auto",
          padding: "10px 0",
          position: "relative",
          // border: "solid 1px red"
        }}
      >
        {/* --- Dimmer --- */}
        <DimmerLoader
          active={props.loadingStatus?.[CARD_NAME] ?? false}
          text={"Loading..."}
          inverted={true}
          size="medium"
        />

        {/* --- Mod Create Project --- */}
        <ModCreateProject
          open={openProjectMod}
          onClose={handleCloseProjectMod}
          onCreateProject={handleCreateProject}
          onEditProject={handleEditProject}
          editMode={isEditMode}
          editProjectId={editProjectId}
          editProjectName={editProjectName}
        />

        {/* --- Mod Delete Project --- */}
        <ModConfirmDelete
          open={openDeleteProjectMod}
          onClose={handleCloseDeleteProjectMod}
          onConfirm={handleConfirmDeleteProject}
          title={"Confirm to delete project"}
          message={`Are you sure you want to delete "${deleteProjectName}"? This action cannot be undone.`}
        />

        {/* --- Mod Delete Series --- */}
        <ModConfirmDelete
          open={openDeleteSeriesMod}
          onClose={handleCloseDeleteSeriesMod}
          onConfirm={handleConfirmDeleteSeries}
          title={"Confirm to delete series"}
          message={`Are you sure you want to delete "${deleteSeries?.name}"? This action cannot be undone.`}
        />

        {/* --- Mod Delete Series (Conflict) --- */}
        <ModConfirmDelete
          open={openConflictDeleteSeriesMod}
          onClose={() => {
            setOpenConflictDeleteSeriesMod(false)
            setDeleteSeries(undefined);
          }}
          onConfirm={handleConfirmForceDeleteSeries}
          title={"This series has associated jobs"}
          message={`This series has associated jobs. Are you sure you want to delete "${deleteSeries?.name}"? This action cannot be undone.`}
          confirmText={"Confirm Delete"}
        />

        {/* --- Mod Rerun Episode --- */}
        <ModConfirmDelete
          open={openRerunEpisodeMod}
          onClose={handleCloseRerunEpisode}
          onConfirm={handleConfirmRestartEpisode}
          title={"Confirm to rerun this episode"}
          message={`Are you sure you want to restart this episode? Existing data will be overwritten.`}
          confirmText={"Rerun"}
          confirmColor="#f382a0"
          confirmHoverColor="#e91e63"
        />

        {/* --- Mod Duplicate Episode --- */}
        <ModConfirmDelete
          open={openDuplicateEpisodeMod}
          onClose={handleCloseDuplicateEpisode}
          onConfirm={handleConfirmDuplicateEpisode}
          title={`Confirm to duplicate this episode`}
          message={`Are you sure you want to duplicate this episode?`}
          confirmText={"Duplicate"}
          confirmColor="#f382a0"
          confirmHoverColor="#e91e63"
        />

        {/* --- Mod Delete Episode --- */}
        <ModConfirmDelete
          open={openDeleteEpisodeMod}
          onClose={handleCloseDeleteEpisode}
          onConfirm={handleConfirmDeleteEpisode}
          title={"Confirm to delete this episode"}
          message={`Are you sure you want to delete this run permanently?`}
        />

        {/* --- Mod Create Series (Website) --- */}
        <ModCreateSeriesWebsite
          open={openCreateWebsiteMod}
          projectId={selectedProject?.id || 0}
          editMode={!!editSeriesData && editSeriesData.series_type === "rss_news"}
          editSeries={editSeriesData}
          defaultScheduleConfig={defaultConfig?.scheduleConfig?.cron ?? undefined}
          defaultCutoffHour={defaultConfig?.cutoffHour ?? 24}
          onClose={handleCloseCreateWebsiteMod}
          onSave={handleSaveSeriesFromModal}
        />

        {/* --- Mod Create Series (Script) --- */}
        <ModCreateSeriesScript
          open={openCreateScriptMod}
          projectId={selectedProject?.id || 0}
          editMode={!!editSeriesData && (
            editSeriesData.series_type === "story2scene" ||
            editSeriesData.series_type === "script2vdo"
          )}
          editSeries={editSeriesData}
          onClose={handleCloseCreateScriptMod}
          onSave={handleSaveSeriesFromModal}
        />

        {/* --- Mod View Series (Website) --- */}
        <ModCreateSeriesWebsite
          open={openViewWebsiteMod}
          projectId={selectedProject?.id || 0}
          viewMode={true}
          editSeries={viewSeriesData}
          defaultScheduleConfig={defaultConfig?.scheduleConfig?.cron ?? undefined}
          defaultCutoffHour={defaultConfig?.cutoffHour ?? 24}
          onClose={handleCloseViewWebsiteMod}
          onSave={() => {}}
        />

        {/* --- Mod View Series (Script) --- */}
        <ModCreateSeriesScript
          open={openViewScriptMod}
          projectId={selectedProject?.id || 0}
          viewMode={true}
          editSeries={viewSeriesData}
          onClose={handleCloseViewScriptMod}
          onSave={() => {}}
        />

        {/* --- Mod Share Project --- */}
        <ModShareProject
          open={openShareProjectMod}
          resourceType={shareResourceType}
          projectId={shareProjectId || 0}
          projectName={shareProjectName}
          seriesId={shareSeriesId}
          seriesName={shareSeriesName}
          runMapId={shareRunMapId}
          runMapName={shareRunMapName}
          members={shareMembers}
          isSearching={isSearhingUsers}
          searchResults={shareSearchResults}
          allOaUsersList={allOaUsersList || []}
          onClose={handleCloseShareProjectMod}
          onSearchUser={handleSearchUser}
          onSave={handleSaveShareMember}
          onRemoveMembers={handleRemoveShareMembers}
        />

        {/* --- Viewer banner: เห็นทุกปุ่มแก้ได้แต่ไม่มีอะไรถูกบันทึก (API เขียนทุกตัวเป็น editor-only) --- */}
        {selectedProject?.id != null && !canEdit && (
          <div
            id="megen-viewer-banner"
            style={{
              margin: "0 4px 8px 2px", // ให้ขอบตรงกับ paddingStyle ของ view ด้านล่าง
              padding: "4px 12px",
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#92400e",
              backgroundColor: "#fef3c7",
              border: "1px solid #fcd34d",
              borderRadius: 8,
            }}
          >
            Viewer (read-only) — คุณดูโปรเจกต์นี้ได้แต่แก้ไม่ได้ ให้เจ้าของโปรเจกต์กด Share เป็น Editor
          </div>
        )}

        {/* --- Main Content --- */}
        {renderContent()}

      </div>
    </div>
  );
};

export default CardMeGenMain;
